import { Achievements } from '@cardsjd/achievements';
import { Advertisement } from '@cardsjd/advertisement';
import { Analytics } from '@cardsjd/analytics';
import { Device } from '@cardsjd/device';
import { EmailFeedback } from '@cardsjd/emailfeedback';
import { FBInstantGame } from '@cardsjd/fbinstantgame';
import { IAP } from '@cardsjd/iap';
import { Localization } from '@cardsjd/localization';
import { MarketPlace } from '@cardsjd/marketplace';
import { Prompter } from '@cardsjd/prompter';
import { RateApp } from '@cardsjd/rateapp';
import { Share } from '@cardsjd/share';
import { Storage } from '@cardsjd/storage';

import i18n from './services/i18n.js';
import Multiplayer from './services/Multiplayer.js';
import { generateId, getRandomAvatarURL } from './services/utilities.js';

import AchievementConfig from './configs/AchievementConfig.js';
import AdvertisementConfig from './configs/AdvertisementConfig.js';
import AppConfig from './configs/AppConfig.js';
import LocalizationConfig from './configs/LocalizationConfig.js';
import StoreConfig from './configs/StoreConfig.js';

export default class App {

	constructor() {
		this.userdata = {};
		this.config = AppConfig;
		this.pageId = 'Loading';
		this.onNavigate = function (_path) { };

		this.onNotification = function (_model) { };

		this.onPopUp = function (_model, _template) { };

		this.debug = this.config.debug;
		this.version = this.config.version;

		//libraries
		this.device = new Device();
		this.localization = new Localization(LocalizationConfig);
		this.marketPlace = new MarketPlace(AppConfig);
		this.rateApp = new RateApp(AppConfig);
		this.emailFeedback = new EmailFeedback(AppConfig);
		this.sharer = new Share(AppConfig);
		this.analytics = new Analytics(AppConfig);
		this.achievements = new Achievements(AchievementConfig);
		this.achievements.onNewAchievement = this.onNewAchievement.bind(this);
		this.prompter = new Prompter(AppConfig);
		this.prompter.onPrompt = this.onPrompt.bind(this);
		this.storage = new Storage(AppConfig);
		this.FBInstantGame = new FBInstantGame();
		this.advertisement = new Advertisement(AdvertisementConfig);

		//get default resource
		this.resource = this.localization.getResource();

		//get device info
		this.os = this.device.getOS();
		this.platform = this.device.getPlatform();
		this.browser = this.device.getBrowser();

		this.iap = new IAP();
		this.iap.onBuy = this.onBuy.bind(this);

		this.multiplayer = new Multiplayer(AppConfig);
		console.log('multiplayer class is', this.multiplayer);
		this.changeListeners = new Set();
	}

	async load() {
		this.navigate('Loading');

		//load userdata
		const data = await this.storage.load('gin.userdata');
		if (data) {
			this.userdata = data;
		}

		//facebook init and load data
		await this.loadFacebookData();

		//set localization resource based on user settings
		if (this.userdata.language) {
			this.resource = this.localization.getResource(this.userdata.language);
		}

		//tracking
		this.analytics.trackStart();
		//todo: firebase track user

		//ask to rate / share
		this.prompter.delayedPrompt();

		//save os in profile
		this.userdata.os = this.device.getOS();
		this.userdata.platform = this.device.getPlatform();
		this.userdata.browser = this.device.getBrowser();
		this.userdata.version = this.version;

		//log game opened
		this.userdata.game_open = (this.userdata.game_open || 0) + 1;
		if (!this.userdata.first_open) {
			this.userdata.first_open = Date.now();
		}

		if (!this.userdata.id) {
			this.userdata.id = generateId();
		}

		if (!this.userdata.name) {
			this.userdata.name = `Player ${this.userdata.id}`;
		}

		if (!this.userdata.avatar) {
			this.userdata.avatar = getRandomAvatarURL();
		}

		//check for achievements
		this.achievements.checkForAchievementAndReward(this.userdata, this.userdata);

		this.iap.init(StoreConfig.productIds).catch(e => console.log('IAP.init() failed', e));

		//todo: Push Notifications

		//todo:black list check

		//set up ads
		//todo: requestTrackingAuthorizationWithExplainer() with explainer text;
		this.advertisement.setUp();

		const proxyHandler = {
			set: (obj, prop, value) => {
				const isDifferent = obj[prop] !== value;
				const success = Reflect.set(obj, prop, value);

				if (success && isDifferent) {
					this.handleDataChange(obj);
				}

				return success;
			},
		};

		this.userdata = new Proxy({
			...this.userdata,
		}, proxyHandler);

		this.navigate('MainMenu');
	}

	navigate(path) {
		this.pageId = path;

		//trigger UI contollers
		if (this.onNavigate) {
			this.onNavigate(path);
		}

		this.navigated(path);
	}

	navigated(path) {
		if (path === '') {
			path = 'MainMenu';
		}

		console.log('navigated', path);

		//todo: firebase track page;

		if (path === 'MainMenu') {
			this.showAd();
			this.save();
		}

		if (path === 'ComputerMenu') {
			this.showAd();
			this.save();
		}

		if (path === 'OnlineMenu') {
			this.showAd();
			this.save();
		}
	}

	onNewAchievement(achievements) {
		//this.showAchievementPopup(achievements);
		this.save();
		for (let i = 0; i < achievements.length; i++) {
			const achievement = achievements[i];
			this.showNotification({
				title: achievement.title,
				description: achievement.description,
				image: 'assets/Achievements/' + achievement.image,
				points: achievement.points,
				type: 'Achievement',
			});
		}

	}

	save() {
		this.storage.save('gin.userdata', this.userdata);
		this.FBInstantGame.saveData(this.userdata);
	}

	showAd(force) {
		if (force) {
			this.advertisement.showInterstitialAd();
		}

		if (this.fullVersion || this.userdata.removeAds) {
			this.advertisement.removeAds = true;
			return;
		}

		this.advertisement.tryShowInterstitialAd();
	}

	showRewardAd() {
		this.advertisement.showRewardedAd();
	}

	rateGame() {
		this.achievements.awardAchivement(this.userdata, 'Rate Game');
		const results = this.rateApp.rate();
		if (!results) {
			this.share();
		}
	}

	email() {
		this.achievements.awardAchivement(this.userdata, 'Send Feedback');
		this.emailFeedback.send(this.userdata);
	}

	share() {
		this.achievements.awardAchivement(this.userdata, 'Share Game');
		this.sharer.share();
	}

	delay(n) {
		return new Promise(function (resolve) {
			setTimeout(resolve, n * 1000);
		});
	}

	showNotification(model) {
		//Basic - { message: "Message", type: "Basic" }
		//Achievement - { message: "Test", description: "Something more", points: 25, type:"Achievement" }
		if (typeof model === 'string') {
			model = {
				message: model,
				type: 'Basic',
			};
		}

		if (this.onNotification) {
			this.onNotification(model);
		}
	}

	showPopUp(model, template) {
		if (this.onPopUp) {
			this.onPopUp(model, template);
		}
	}

	showEnjoyGame(message) {
		if (!message) {
			message = i18n.t('Having fun?');
		}

		this.showPopUp({
			message: message,
			onYes: this.showAskRate.bind(this),
		}, 'YesNoDialog');
	}

	showShare(message) {
		if (!message) {
			message = i18n.t('Share the app?');
		}

		this.showPopUp({
			message: message,
			onYes: this.share.bind(this),
		}, 'YesNoDialog');
	}

	showAskRate(message) {
		if (!message) {
			message = i18n.t('Rate the app?');
		}

		this.showPopUp({
			message: message,
			onYes: this.rateGame.bind(this),
		}, 'YesNoDialog');
	}

	onPrompt(message, os) {
		if (os === 'FBMessenger') {
			this.showShare(message);
		} else {
			//ask if like game then ask to rate
			this.showEnjoyGame(message);
		}

		this.rateApp.rateNative();
	}

	setIsMuted(isMuted) {
		this.userdata.isMuted = isMuted;
		this.save();
	}

	onBuy(boughtProductId) {
		console.info('onBuy results', boughtProductId);

		(async () => {
			const catalog = await this.iap.getCatalog();

			for (const product of catalog) {
				if (product.productId === boughtProductId) {
					this.handleBoughtProducts([product]);
					break;
				}
			}
		})();
	}

	handleBoughtProducts(products) {
		for (const product of products) {
			if (product.productId.indexOf('removeads') > -1) {
				if (!this.userdata.removeAds) {
					this.showNotification('Ads have been removed');
				}

				this.userdata.removeAds = true;
			}
		}

		this.save();
	}

	setRemoveAds(shouldRemoveAds) {
		this.userdata.removeAds = shouldRemoveAds;
		this.save();
	}

	setLanguage(newLanguage) {
		this.userdata.language = newLanguage;
		this.save();
	}

	async loadFacebookData() {
		if (!this.FBInstantGame.isOnFacebook) {
			return;
		}

		await this.FBInstantGame.initialize();

		//load fb user data if no local data
		if (!this.userdata || JSON.stringify(this.userdata) === '{}') {
			const fbUserData = await this.FBInstantGame.loadData('userdata');
			if (fbUserData) {
				this.userdata = fbUserData;
			}
		}

		//load fb profile data
		const fbProfile = await this.FBInstantGame.getProfile();
		if (fbProfile) {
			if (fbProfile.id) {
				this.userdata.facebookId = fbProfile.id;
			}

			if (!this.userdata.name && fbProfile.name) {
				this.userdata.name = fbProfile.name;
			}

			if (!this.userdata.avatar && fbProfile.pic) {
				this.userdata.avatar = fbProfile.pic;
			}

			if (fbProfile.pic) {
				this.userdata.avatar_facebook = fbProfile.pic;
			}

			if (!this.userdata.locale && fbProfile.locale) {
				this.userdata.locale = fbProfile.locale;
			}
		}

		//check for invites
		const invite = await this.FBInstantGame.getInvite();
		if (invite) {
			this.showNotification(i18n.t('You have been invited to play a game'));
		}
	}

	addChangeListener(listener) {
		this.changeListeners.add(listener);
	}

	removeChangeListener(listener) {
		this.changeListeners.delete(listener);
	}

	handleDataChange(newData) {
		const copyData = structuredClone(newData);

		for (const listener of this.changeListeners) {

			if (typeof listener === 'function') {
				listener(copyData);
			}
		}
	}
}
